import {
  ArchiveIcon,
  ArrowUpDownIcon,
  EllipsisIcon,
  EyeIcon,
  EyeOffIcon,
  Layers3Icon,
  ListFilterIcon,
  PinIcon,
  PinOffIcon,
  RotateCcwIcon,
} from "lucide-react";

import { PraxaConnectorMonochromeLogo } from "../praxa/PraxaConnectorLogo";
import {
  Menu,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
  MenuTrigger,
} from "../ui/menu";
import { SidebarMenuAction } from "../ui/sidebar";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import type {
  PraxaSidebarChannelFilter,
  PraxaSidebarChannelGrouping,
  PraxaSidebarChannelPreferences,
  PraxaSidebarChannelRow,
  PraxaSidebarChannelSort,
} from "./PraxaSidebarChannels.logic";

export const PRAXA_SIDEBAR_CHANNEL_FILTER_LABELS: Record<PraxaSidebarChannelFilter, string> = {
  all: "All channels",
  praxa: "Praxa AI",
  slack: "Slack",
  pinned: "Pinned",
  hidden: "Hidden",
  archived: "Archived",
};
const GROUPING_LABELS: Record<PraxaSidebarChannelGrouping, string> = {
  none: "None",
  source: "Source",
};
const SORT_LABELS: Record<PraxaSidebarChannelSort, string> = {
  default: "Default",
  name: "Name",
  recent: "Recently updated",
};

type SetPreferences = (
  update: (current: PraxaSidebarChannelPreferences) => PraxaSidebarChannelPreferences,
) => void;

export function PraxaSidebarChannelViewOptions({
  preferences,
  setPreferences,
}: {
  readonly preferences: PraxaSidebarChannelPreferences;
  readonly setPreferences: SetPreferences;
}) {
  return (
    <Menu>
      <Tooltip>
        <TooltipTrigger
          render={
            <MenuTrigger
              aria-label="Channel view options"
              className="inline-flex size-6 cursor-pointer items-center justify-center rounded-md text-sidebar-muted-foreground outline-none hover:bg-sidebar-row-hover hover:text-sidebar-foreground focus-visible:ring-1 focus-visible:ring-ring"
            />
          }
        >
          <EllipsisIcon aria-hidden className="size-4" />
        </TooltipTrigger>
        <TooltipPopup side="top">Channel view options</TooltipPopup>
      </Tooltip>
      <MenuPopup align="end" className="w-[220px]" side="bottom">
        <MenuSub>
          <MenuSubTrigger>
            <ListFilterIcon aria-hidden />
            Filter
            <span className="ml-auto max-w-20 truncate text-xs text-muted-foreground">
              {PRAXA_SIDEBAR_CHANNEL_FILTER_LABELS[preferences.filter]}
            </span>
          </MenuSubTrigger>
          <MenuSubPopup className="w-44">
            <MenuRadioGroup
              onValueChange={(value) =>
                setPreferences((current) => ({
                  ...current,
                  filter: value as PraxaSidebarChannelFilter,
                }))
              }
              value={preferences.filter}
            >
              {(
                Object.entries(PRAXA_SIDEBAR_CHANNEL_FILTER_LABELS) as [
                  PraxaSidebarChannelFilter,
                  string,
                ][]
              ).map(([value, label]) => (
                <MenuRadioItem closeOnClick key={value} value={value}>
                  {label}
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>
          </MenuSubPopup>
        </MenuSub>
        <MenuSub>
          <MenuSubTrigger>
            <Layers3Icon aria-hidden />
            Group by
            <span className="ml-auto max-w-20 truncate text-xs text-muted-foreground">
              {GROUPING_LABELS[preferences.grouping]}
            </span>
          </MenuSubTrigger>
          <MenuSubPopup className="w-40">
            <MenuRadioGroup
              onValueChange={(value) =>
                setPreferences((current) => ({
                  ...current,
                  grouping: value as PraxaSidebarChannelGrouping,
                }))
              }
              value={preferences.grouping}
            >
              {(Object.entries(GROUPING_LABELS) as [PraxaSidebarChannelGrouping, string][]).map(
                ([value, label]) => (
                  <MenuRadioItem closeOnClick key={value} value={value}>
                    {label}
                  </MenuRadioItem>
                ),
              )}
            </MenuRadioGroup>
          </MenuSubPopup>
        </MenuSub>
        <MenuSub>
          <MenuSubTrigger>
            <ArrowUpDownIcon aria-hidden />
            Sort by
            <span className="ml-auto max-w-20 truncate text-xs text-muted-foreground">
              {SORT_LABELS[preferences.sort]}
            </span>
          </MenuSubTrigger>
          <MenuSubPopup className="w-44">
            <MenuRadioGroup
              onValueChange={(value) =>
                setPreferences((current) => ({
                  ...current,
                  sort: value as PraxaSidebarChannelSort,
                }))
              }
              value={preferences.sort}
            >
              {(Object.entries(SORT_LABELS) as [PraxaSidebarChannelSort, string][]).map(
                ([value, label]) => (
                  <MenuRadioItem closeOnClick key={value} value={value}>
                    {label}
                  </MenuRadioItem>
                ),
              )}
            </MenuRadioGroup>
          </MenuSubPopup>
        </MenuSub>
        <MenuSeparator />
        <MenuItem
          onClick={() =>
            setPreferences((current) => ({
              ...current,
              filter: "all",
              grouping: "none",
              sort: "default",
            }))
          }
        >
          <RotateCcwIcon aria-hidden />
          Reset view
        </MenuItem>
      </MenuPopup>
    </Menu>
  );
}

export function PraxaSidebarChannelRowActions({
  onArchive,
  onHide,
  onOpenSource,
  onPin,
  onRestore,
  pinning,
  row,
  working,
}: {
  readonly onArchive: () => void;
  readonly onHide: () => void;
  readonly onOpenSource: () => void;
  readonly onPin: () => void;
  readonly onRestore: () => void;
  readonly pinning: boolean;
  readonly row: PraxaSidebarChannelRow;
  readonly working: boolean;
}) {
  return (
    <Menu>
      <MenuTrigger
        render={
          <SidebarMenuAction
            aria-label={`Actions for ${row.name}`}
            className="size-6 w-6 rounded-md"
            showOnHover
          />
        }
      >
        <EllipsisIcon aria-hidden className="size-4" />
      </MenuTrigger>
      <MenuPopup align="start" className="w-48" side="right">
        <MenuItem disabled={pinning} onClick={onPin}>
          {row.pinned ? <PinOffIcon aria-hidden /> : <PinIcon aria-hidden />}
          {row.source === "slack"
            ? row.pinned
              ? "Unpin from Work and sidebar"
              : "Pin in Work and sidebar"
            : row.pinned
              ? "Unpin from sidebar"
              : "Pin in sidebar"}
        </MenuItem>
        <MenuItem onClick={onHide}>
          {row.hidden ? <EyeIcon aria-hidden /> : <EyeOffIcon aria-hidden />}
          {row.hidden ? "Show in sidebar" : "Hide from sidebar"}
        </MenuItem>
        {row.source === "slack" && row.sourceUrl ? (
          <MenuItem onClick={onOpenSource}>
            <PraxaConnectorMonochromeLogo name="Slack" slug="slack" />
            Open in Slack
          </MenuItem>
        ) : null}
        {row.source === "praxa" && row.canArchive ? <MenuSeparator /> : null}
        {row.source === "praxa" && row.canArchive && row.archived ? (
          <MenuItem disabled={working} onClick={onRestore}>
            <RotateCcwIcon aria-hidden />
            Restore channel
          </MenuItem>
        ) : null}
        {row.source === "praxa" && row.canArchive && !row.archived ? (
          <MenuItem disabled={working} onClick={onArchive} variant="destructive">
            <ArchiveIcon aria-hidden />
            Archive channel…
          </MenuItem>
        ) : null}
      </MenuPopup>
    </Menu>
  );
}
